/**
 * Client-side registration guard — mirrors getlanceapi app/Helpers/registrationGuard.js
 * Defense-in-depth UX only; API remains the source of truth.
 */

const PLATFORM_DOMAINS = ["getlance.com", "getlance.com.br"];

const RESERVED_OR_DISPOSABLE_DOMAINS = [
  "example.com",
  "example.net",
  "example.org",
  "test.com",
  "test.org",
  "localhost",
  "localdomain",
  "invalid",
  "local",
  "mailinator.com",
  "guerrillamail.com",
  "10minutemail.com",
  "tempmail.com",
  "yopmail.com",
  "trashmail.com",
  "sharklasers.com",
  "discard.email",
  "fakeinbox.com",
];

const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 8;

const MSG_INVALID_EMAIL =
  "Informe um e-mail válido para continuar o cadastro.";
const MSG_DOMAIN_BLOCKED =
  "Este domínio de e-mail não é permitido para cadastro público. Use um e-mail pessoal ou corporativo válido.";
const MSG_INVALID_NAME = "Informe um nome válido (sem HTML).";

export function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function getEmailDomain(email) {
  const normalized = normalizeEmail(email);
  const at = normalized.lastIndexOf("@");
  if (at < 0) return "";
  return normalized.slice(at + 1);
}

function domainMatchesList(domain, list) {
  const d = String(domain || "").trim().toLowerCase();
  if (!d || !Array.isArray(list)) return false;

  for (const item of list) {
    const blocked = String(item || "").trim().toLowerCase();
    if (!blocked) continue;
    if (d === blocked || d.endsWith("." + blocked)) return true;
  }
  return false;
}

/**
 * @returns {boolean} true when format is valid and domain is not blocked
 */
export function isEmailDomainAllowed(email) {
  const normalized = normalizeEmail(email);
  if (!normalized || !EMAIL_FORMAT_RE.test(normalized)) return false;

  const domain = getEmailDomain(normalized);
  if (
    domainMatchesList(domain, PLATFORM_DOMAINS) ||
    domainMatchesList(domain, RESERVED_OR_DISPOSABLE_DOMAINS)
  ) {
    return false;
  }
  return true;
}

/**
 * @returns {string|null} Portuguese reason, or null if ok
 */
export function getEmailDomainBlockReason(email) {
  const normalized = normalizeEmail(email);
  if (!normalized || !EMAIL_FORMAT_RE.test(normalized)) {
    return MSG_INVALID_EMAIL;
  }

  const domain = getEmailDomain(normalized);
  const blocked =
    domainMatchesList(domain, PLATFORM_DOMAINS) ||
    domainMatchesList(domain, RESERVED_OR_DISPOSABLE_DOMAINS);

  if (blocked) return MSG_DOMAIN_BLOCKED;
  return null;
}

/**
 * Strip tags/control chars; collapse whitespace; cap at 80 chars.
 */
export function sanitizeDisplayNameClient(name) {
  return String(name || "")
    .trim()
    .replace(/<\/?[^>]+>/g, "")
    .replace(/[\x00-\x1F]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}

/**
 * Valid when sanitized length is 2–80 and no leftover < or >.
 */
export function isSafeDisplayName(name) {
  const sanitized = sanitizeDisplayNameClient(name);
  if (sanitized.length < 2) return false;
  if (sanitized.indexOf("<") !== -1 || sanitized.indexOf(">") !== -1) return false;
  return true;
}

/**
 * @param {{ name?: string, email?: string, password?: string }} fields
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateRegisterForm({ name, email, password } = {}) {
  const errors = [];

  if (!String(name || "").trim()) {
    errors.push("O nome é obrigatório.");
  } else if (!isSafeDisplayName(name)) {
    errors.push(MSG_INVALID_NAME);
  }

  const emailReason = getEmailDomainBlockReason(email);
  if (emailReason) {
    errors.push(emailReason);
  }

  if (!password) {
    errors.push("A senha é obrigatória.");
  } else if (String(password).length < MIN_PASSWORD_LENGTH) {
    errors.push(`A senha deve ter no mínimo ${MIN_PASSWORD_LENGTH} caracteres.`);
  }

  return { ok: errors.length === 0, errors };
}

export {
  PLATFORM_DOMAINS,
  RESERVED_OR_DISPOSABLE_DOMAINS,
  MIN_PASSWORD_LENGTH,
  MSG_INVALID_EMAIL,
  MSG_DOMAIN_BLOCKED,
  MSG_INVALID_NAME,
};
